import { GlobalRole, ResultFailType } from '@itm/shared-frontend';

export { ResultFailType } from '@itm/shared-frontend';
export type {
  GlobalRole,
  SelectOption,
  ServerError,
  ServerFormErrors,
  ExternalFilters,
  TanstackTableChangeParams,
  // Data Access Permission
  ErrorWithType,
  SchemeFromProductDto,
  ProductDto,
  CreateDataAccessDto,
  ShortSchemeResponse,
  ProductSchemeResponse,
} from '@itm/shared-frontend/lib/types';

export enum TargetPlatform {
  AWS = 'AWS',
  AZURE = 'AZURE',
}

export enum Role {
  SuperAdmin = 'SuperAdmin',
  Support = 'Support',
  Analyst = 'DashboardISP_Analyst',
  Viewer = 'DashboardISP_Viewer',
  ClientAdmin = 'DashboardISP_ClientAdmin',
  Manager = 'DashboardISP_Manager',
  CustomerOperations = 'DashboardISP_CustomerOperations',
}

export type CompanyResponse = {
  id: string;
  name: string;
};

export enum AccruedAmountUnavailabilityReason {
  ThirdParty = 'ThirdParty',
  DBCalculation = 'DBCalculation',
  DCCalculation = 'DCCalculation',
  MemberAction = 'MemberAction',
  NewMemberTransfer = 'NewMemberTransfer',
  NewMember = 'NewMember',
  PPFAssessment = 'PPFAssessment',
  TransactionOutstanding = 'TransactionOutstanding',
  WindingUp = 'WindingUp',
}

export enum EriUnavailabilityReason {
  ThirdParty = 'ThirdParty',
  DBNotRequired = 'DBNotRequired',
  DBCalculation = 'DBCalculation',
  DCCalculation = 'DCCalculation',
  DCHybridAmount = 'DCHybridAmount',
  DCHybridRetirementDate = 'DCHybridRetirementDate',
  MemberAction = 'MemberAction',
  NewMemberTransfer = 'NewMemberTransfer',
  NewMember = 'NewMember',
  PPFAssessment = 'PPFAssessment',
  TransactionOutstanding = 'TransactionOutstanding',
  WindingUp = 'WindingUp',
}

export enum FullMatchNoValueDataResponse {
  ReturnNoData = 'ReturnNoData',
  ReturnDbcDcc = 'ReturnDbcDcc',
  ReturnValuationUnavailabilityReason = 'ReturnValuationUnavailabilityReason',
  ReturnContactUs = 'ReturnContactUs',
  ReturnTempSystemError = 'ReturnTempSystemError',
}

export enum PpfNoValueDataResponse {
  ReturnNoData = 'ReturnNoData',
  ReturnPpfForBoth = 'ReturnPpfForBoth',
}

export enum ViewValueGroupingIllustrationDatePreference {
  MostRecentAccruedDate = 'MostRecentAccruedDate',
  MostRecentEriDate = 'MostRecentEriDate',
  MostRecentDate = 'MostRecentDate',
}

export type CompanyConfigResponse = {
  id: string;
  name: string | null;
  pdpPublish: boolean;
  altusClientId: number | null;
  altusUserName: string | null;
  canEditPDPPublish: boolean;
  isAutoDeregisterPossibleMatches: boolean;
  autoDeregisterPossibleMatchesDays: number;
  isMatchesAutoReRunEnabled: boolean;
  valuationAgeTriggerInMonths: number | null;
  defaultDateFilterValue: string | null;
  fullMatchNoValueDataAccruedResponse: AccruedAmountUnavailabilityReason | null;
  fullMatchNoValueDataEriResponse: EriUnavailabilityReason | null;
  fullMatchNoValueDataResponse: FullMatchNoValueDataResponse | null;
  ppfNoValueDataResponse: PpfNoValueDataResponse | null;
  viewValueGroupingIllustrationDatePreference: ViewValueGroupingIllustrationDatePreference | null;
  viewValueGroupingOneOfEach: boolean;
  viewValueGroupingTwoOfEachDifferentDates: boolean;
  viewValueGroupingTwoOfEachSameDateTwoBenefitTypes: boolean;
  isPgpEncryptionEnabled: boolean;
};

export type SchemeResponse = {
  id: string;
  name: string;
  companyId: string;
};

export type SchemeConfigurationShortResponse = {
  id: string;
  name: string | null;
  pdpRegistrationId: string | null;
  pdpPublish: boolean;
};

export enum GetPDPRegistrationResponseStatus {
  Draft = 'Draft',
  Success = 'Success',
  Initiated = 'Initiated',
  InProgress = 'InProgress',
  Failed = 'Failed',
  NotRegistered = 'NotRegistered',
}

export enum GetAltusSchemeResponseStates {
  Pending = 'Pending',
  Enabled = 'Enabled',
  Disabled = 'Disabled',
  NotShared = 'NotShared',
  Unavailable = 'Unavailable',
}

export type SchemeConfigurationResponse = {
  id: string;
  name: string;
  pdpPublish: boolean;
  pdpRegistrationStatus: GetPDPRegistrationResponseStatus;
  schemeStatus: GetAltusSchemeResponseStates;
  pdpRegistrationId: string | null;
  isPDPRegistered: boolean;
  connectionDate: string | null;
};

export type SchemeConfigurationResponsePagedResponse = {
  totalCount: number;
  items: SchemeConfigurationResponse[] | null;
};

export enum CompanyConfigurationStatus {
  MatchingPolicyCreated = 'MatchingPolicyCreated',
  ClientInformationUpdated = 'ClientInformationUpdated',
}

export type CompanyConfigurationResponse = {
  id: string;
  name: string | null;
  statuses: CompanyConfigurationStatus[];
  pdpPublish: boolean;
  isComplexMatchingEnabled: boolean;
  isPgpEncryptionEnabled: boolean;
  createdDate: string;
  updatedDate: string | null;
};

export enum ComplexMatchingAlgorithm {
  AllowSimilarValues = 'AllowSimilarValues',
  AllowSwappedNeighbouringCharacters = 'AllowSwappedNeighbouringCharacters',
  AllowXDayDateTolerance = 'AllowXDayDateTolerance',
  AllowAmericanDateFormat = 'AllowAmericanDateFormat',
  /**
   * @deprecated currently Equisoft (Altus) does not support these algorithms
   */
  AllowSwappedNames = 'AllowSwappedNames',
  /**
   * @deprecated currently Equisoft (Altus) does not support these algorithms
   */
  IgnoreDoubleBarrelledNames = 'IgnoreDoubleBarrelledNames',
}

export type ComplexMatchingAlgorithmsDto = {
  companyId: string;
  firstNameIdentifiers: ComplexMatchingAlgorithm[];
  lastNameIdentifiers: ComplexMatchingAlgorithm[];
  dobIdentifiers: ComplexMatchingAlgorithm[];
  niNoIdentifiers: ComplexMatchingAlgorithm[];
  postCodeIdentifiers: ComplexMatchingAlgorithm[];
  mobileNumberIdentifiers: ComplexMatchingAlgorithm[];
  emailIdentifiers: ComplexMatchingAlgorithm[];
  comment?: string;
};

export type ComplexMatchingStateResponse = {
  isComplexMatchingEnabled: boolean;
  isComplexMatchingDisableAvailable: boolean;
  canDisableComplexMatching: boolean;
};

export type RuleWithComplexIdentifier = {
  description: string | null;
  isComplex: boolean;
  isDigitalVerified: boolean;
};

export type GetRulesToReviewResponse = {
  matchingPolicyId: string | null;
  matchingPolicyGroupId: string | null;
  matchingRuleId: string | null;
  matchingPolicyVersion: number;
  canEditPolicy: boolean;
  matchingPolicyStatus: MatchingPolicyStatus;
  matchingPolicyVersionStatus: MatchingPolicyApprovalStatus;
  matchingPolicyName: string | null;
  matchingRuleName: string | null;
  schemesNames: string[] | null;
  matchingRuleDetails: RuleWithComplexIdentifier[] | null;
};

export type CompanyConfigurationsWithCount = {
  items: CompanyConfigurationResponse[];
  totalCount: number;
};

export type UpdateCompanyConfigurationDto = {
  companyId: string;
  altusClientId: number;
  altusUserName: string;
  comment: string;
};

export enum AddressTypeEnum {
  Current = 'Current',
  Previous = 'Previous',
  Other = 'Other',
}

export enum AltNameType {
  Changed = 'Changed',
  Maiden = 'Maiden',
}

export enum OriginType {
  WM = 'WM',
  WC = 'WC',
  WT = 'WT',
  PT = 'PT',
  PM = 'PM',
  PC = 'PC',
  A = 'A',
}

export enum PensionType {
  AVC = 'AVC',
  CB = 'CB',
  CDC = 'CDC',
  DB = 'DB',
  DC = 'DC',
  HYB = 'HYB',
}

export enum MembershipStatusCode {
  A = 'A',
  I = 'I',
}

export enum PensionStatus {
  Active = 'Active',
  Inactive = 'Inactive',
}

export enum MatchType {
  Full = 'Full',
  Maybe = 'Maybe',
}

export enum AdministratorContactPreference {
  Website = 'Website',
  Email = 'Email',
  Phone = 'Phone',
  Mail = 'Mail',
}

export enum AdministratorPhoneNumberType {
  Main = 'Main',
  NonUK = 'NonUK',
  SMS = 'SMS',
  WelshSpeaking = 'WelshSpeaking',
  WhatsApp = 'WhatsApp',
}

export enum TestType {
  FindMatching = 'FindMatching',
  FindMember = 'FindMember',
}

export type FindAddressModel = {
  type: AddressTypeEnum;
  postcode?: string;
  countryCode?: string;
  addressLine1: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  addressLine5?: string;
};

export type FindAltForenameModel = {
  name: string;
  isNameVerified: boolean;
};

export type FindAltSurnameModel = {
  name: string;
  type: AltNameType;
  isNameVerified: boolean;
};

export type FindAltEmailModel = {
  email: string;
  isEmailAddressVerified: boolean;
};

export type FindAltPhoneModel = {
  number: string;
  isPhoneNumberVerified: boolean;
};

export type FindMatchingPolicyResponse = {
  name: string | null;
  version: number;
  status: MatchingPolicyApprovalStatus;
};

export type FindMemberModel = {
  companyId: string;
  findCorrelationId?: string;
  forename: string;
  forenameVerified?: boolean;
  surname: string;
  surnameVerified?: boolean;
  dateOfBirth: string;
  dateOfBirthVerified?: boolean;
  matchingPolicyId?: string;
  matchingPolicyVersionId?: string;
  niNumber?: string;
  niNumberVerified?: boolean;
  niNumberNotApplicable?: boolean;
  niNumberNotApplicableVerified?: boolean;
  phoneNumber?: string;
  phoneNumberVerified?: boolean;
  email?: string;
  emailVerified?: boolean;
  addresses?: FindAddressModel[];
  altForenames?: FindAltForenameModel[];
  altSurnames?: FindAltSurnameModel[];
  altEmails?: FindAltEmailModel[];
  altPhones?: FindAltPhoneModel[];
};

export type MappedAltusFindMatchingDto = {
  matchedRule: MatchedRuleResponse;
  matchingPolicy: FindMatchingPolicyResponse;
  section: SectionResponse;
  clientReference: string | null;
  person: AltusPersonDto;
  estimatedRetirementIncomes: AltusEstimatedRetirementIncomes[] | null;
  accruedAmounts: AltusAccruedAmounts[] | null;
  employers: AltusEmployer[] | null;
  linkReference: string | null;
  providerOrSchemeName: string | null;
  additionalProviderOrSchemeName: string | null;
  additionalProviderOrSchemeNameType: string | null;
  origin: string | null;
  reference: string | null;
  type: string | null;
  retirementDate: string | null;
  sectionClientReference: string | null;
  startDate: string | null;
  status: string | null;
  unavailabilityReason: string | null;
  unavailabilityReasonDescription: string | null;
  matchBehaviourOverride: string | null;
  creationTimestamp: string;
  creationUsername: string | null;
  lastUpdatedTimestamp: string | null;
  lastUpdatedUsername: string | null;
  internalId: number;
};

export type DeregisterMatchingDetailsDto = {
  membershipMatchingId: string;
  companyId: string;
  comment: string;
};

export type DemoteMatchingDetailsDto = DeregisterMatchingDetailsDto;

export type ConfirmMembershipMatchingDto = {
  membershipMatchingId: string;
  companyId: string;
  comment?: string;
};

export type EditMembershipMatchingDto = ConfirmMembershipMatchingDto & {
  adminSourceData: AdminSourceDataDto;
};

export type MemberMath = { findRequestIdentifier: string };

export type AltusAlternateSurname = {
  type: string | null;
  name: string | null;
};

export type AltusAlternateForename = {
  name: string | null;
};

export type AltusAlternateEmail = {
  email: string | null;
};

export type AltusAlternatePhoneNumber = {
  phoneNumber: string | null;
};

export type AltusPersonDto = {
  addresses: AltusAddressesDto[] | null;
  alternateSurnames: AltusAlternateSurname[] | null;
  alternateForenames: AltusAlternateForename[] | null;
  alternateEmails: AltusAlternateEmail[] | null;
  alternatePhoneNumbers: AltusAlternatePhoneNumber[] | null;
  dateOfBirth: string;
  email: string | null;
  forename: string | null;
  hasNoNationalInsuranceNumber: boolean;
  nationalInsuranceNumber: string | null;
  phoneNumber: string | null;
  surname: string | null;
};

export type AltusAddressesDto = {
  clientReference: string | null;
  type: AddressTypeEnum | null;
  countryCode: string | null;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  line4: string | null;
  line5: string | null;
  postcode: string | null;
  isAddressAsserted: boolean;
};

export type AltusEstimatedRetirementIncomes = {
  clientReference: string | null;
  pensionClientReference: string | null;
  amountType: string | null;
  basis: string | null;
  illustrationDate: string | null;
  endDate: string | null;
  hasSurvivorBenefits: boolean | null;
  hasSafeguardedBenefits: boolean | null;
  monthlyAmount: number | null;
  payableDate: string | null;
  benefitType: string | null;
  pot: number | null;
  unavailabilityReason: string | null;
  warnings: string[] | null;
  willIncrease: boolean | null;
  dcLessThan120pa: boolean | null;
  illustrationReference: string | null;
  amount: number | null;
};

export type AltusAccruedAmounts = {
  clientReference: string | null;
  pensionClientReference: string | null;
  amountType: string | null;
  basis: string | null;
  illustrationDate: string | null;
  endDate: string | null;
  hasSurvivorBenefits: boolean | null;
  hasSafeguardedBenefits: boolean | null;
  monthlyAmount: number | null;
  payableDate: string | null;
  benefitType: string | null;
  pot: number | null;
  unavailabilityReason: string | null;
  warnings: string[] | null;
  willIncrease: boolean | null;
  dcLessThan120pa: boolean | null;
  illustrationReference: string | null;
  amount: number | null;
};

export type AltusEmployer = {
  clientReference: string | null;
  name: string | null;
  status: string | null;
  employmentStartDate: string | null;
  employmentEndDate: string | null;
};

export type MatchedRuleResponse = {
  matchType: string | null; //on the MappedAltusFindMatchingDto, this field is compared with MatchType enum
  ruleDisplayName: string | null;
  ruleClientReference: string | null;
  ruleId: number | null;
  ruleSetDisplayName: string | null;
  ruleSetClientReference: string | null;
  ruleSetId: number | null;
  types: string[] | null;
};

export type SectionResponse = {
  administratorContactDetails: AdministratorContactDetailsResponse;
  adminContactDetailsClientReference: string | null;
  possibleAdminContactDetailsClientReference: string | null;
  annualReportUrl: string | null;
  clientReference: string | null;
  costsAndChargesUrl: string | null;
  implementationStatementUrl: string | null;
  investmentPrincipalsUrl: string | null;
  schemeClientReference: string | null;
};

export type AdministratorContactDetailsResponse = {
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  addressLine4: string | null;
  addressLine5: string | null;
  clientReference: string | null;
  isUrlPreferred: boolean;
  isPostalAddressPreferred: boolean;
  isEmailPreferred: boolean;
  countryCode: string | null;
  displayName: string | null;
  email: string | null;
  name: string | null;
  postalName: string | null;
  postcode: string | null;
  url: string | null;
  creationTimestamp: string;
  creationUsername: string | null;
  lastUpdatedTimestamp: string | null;
  lastUpdatedUsername: string | null;
  administratorPhoneNumbers: AdministratorPhoneNumber[] | null;
};

export type AdministratorPhoneNumber = {
  phoneNumber: string;
  isTypeWhatsApp: boolean | null;
  isTypeMain: boolean | null;
  isTypeNonUkNumber: boolean | null;
  isTypeSms: boolean | null;
  isTypeWelshSpeaking: boolean | null;
  isPhoneNumberPreferred: boolean | null;
};

export type ProductInfoResponse = {
  id: string;
  description: string | null;
  version: string | null;
};

export type AuditResponse = {
  id: string;
  userName: string;
  userId: string;
  eventType: string;
  entityType: string;
  dateTime: string;
  oldValues: string;
  newValues: string;
  affectedColumn: string;
  entityId: string;
};

export type GetAuditResponse = {
  totalCount: number;
  auditRecords: AuditResponse[];
};

export type UserPermissionResponse = {
  userId: string;
  companyId: string;
  companyName: string;
  schemeId: string;
  schemeName: string;
  productId: string;
  productName: string;
  productRoleId: string;
  productRoleName: GlobalRole;
  userProductRoleId: string;
  assignmentDate: string;
};

export enum MatchingPolicyType {
  Template = 'Template',
  Default = 'Default',
  Alternative = 'Alternative',
}

export enum MatchingPolicyStatus {
  Draft = 'Draft',
  Active = 'Active',
  ApprovalPending = 'ApprovalPending',
}

export type MatchingPolicyDetailsResponse = {
  id: string;
  name: string;
  version: number;
  description: string | null;
};
export type MatchingPolicyResponse = {
  id: string;
  name: string;
  status: MatchingPolicyStatus;
  type: MatchingPolicyType;
  version: number;
  description: string | null;
  createdDate: string;
  updatedDate: string | null;
  canEditPolicy: boolean;
  lastPendingVersionId: string | null;
  isPolicyCorrect: boolean | null;
};

export type MatchingPolicyResponseWithCount = {
  totalCount: number;
  items: MatchingPolicyResponse[] | null;
  canViewButtonReview: boolean;
  canCreateNewPolicy: boolean;
};

export enum MatchingPolicyApprovalStatus {
  Draft = 'Draft',
  Pending = 'Pending',
  Approved = 'Approved',
  Rejected = 'Rejected',
}

export enum MatchingPolicySyncStatus {
  NotStarted = 'NotStarted',
  Success = 'Success',
  Failed = 'Failed',
}

export type MatchingPolicyVersionResponse = {
  id: string;
  status: MatchingPolicyApprovalStatus;
  version: number;
  createdByUserName: string | null;
  reviewerUserName: string | null;
  createdDate: string;
  updatedDate: string | null;
  syncStatus: MatchingPolicySyncStatus;
};

export type MatchingPolicyVersionsWithCount = {
  items: MatchingPolicyVersionResponse[];
  totalCount: number;
};

export type CreateDraftPolicyDto = {
  companyId: string;
  matchingPolicyId: string;
};

export type ConfirmDraftPolicyModel = {
  companyId: string;
  matchingPolicyVersionId: string;
  name: string;
  comment: string;
};

export type DeleteDraftPolicyDto = {
  companyId: string;
  matchingPolicyVersionId: string;
};

export type MatchingPolicyApproveModel = {
  companyId: string;
  matchingPolicyVersionId: string;
  comment?: string;
};

export type MatchingPolicyRejectModel = MatchingPolicyApproveModel;

export type SchemePolicyResponse = {
  schemeId: string;
  schemeName: string | null;
  matchingPolicyName: string | null;
  updateDate: string;
};

export type SchemeByMatchingPolicyResponseWithCount = {
  items: SchemePolicyResponse[];
  totalCount: number;
};

export enum MatchingRuleCriteriaType {
  Surname = 'Surname',
  Forename = 'Forename',
  DOB = 'DOB',
  NINO = 'NINO',
  Postcode = 'Postcode',
  Email = 'Email',
  Mobile = 'Mobile',
}

export enum MatchingRuleCriteriaComparisonType {
  Exact = 'Exact',
  ExactToAny = 'ExactToAny',
  ExactToCurrent = 'ExactToCurrent',
  ExactAndValid = 'ExactAndValid',
  ExactFirstCharacter = 'ExactFirstCharacter',
  ExactFirst8Characters = 'ExactFirst8Characters',
  ExactFirst8CharactersAndValid = 'ExactFirst8CharactersAndValid',
  ExactLast10NumbersToAny = 'ExactLast10NumbersToAny',
  Fuzzy = 'Fuzzy',
  FuzzyToAny = 'FuzzyToAny',
  ExactFirstCharacterToCurrent = 'ExactFirstCharacterToCurrent',
  ExactFirstCharacterToAny = 'ExactFirstCharacterToAny',
  FuzzyToCurrent = 'FuzzyToCurrent',
  FuzzyAndValid = 'FuzzyAndValid',
  FuzzyFirst8Characters = 'FuzzyFirst8Characters',
  FuzzyFirst8CharactersAndValid = 'FuzzyFirst8CharactersAndValid',
  FuzzyLast10NumbersToAny = 'FuzzyLast10NumbersToAny',
}

export type MatchingRuleCriteriaResponse = {
  id: string;
  type: MatchingRuleCriteriaType;
  comparisonType: MatchingRuleCriteriaComparisonType;
  mustBeVerifiedByDigitalIdentityService: boolean;
  associationConfirmed: boolean | null;
  isPolicyCorrect: boolean | null;
};

export type MatchingRuleResponse = {
  id: string;
  name: string;
  order: number;
  isEnabled: boolean;
  matchingRuleTemplateId: string;
  isPolicyCorrect: boolean | null;
  matchingRuleCriteries: MatchingRuleCriteriaResponse[];
};

export type CriteriaComparisonTypeResponse = {
  comparisonType: MatchingRuleCriteriaComparisonType;
  description: string | null;
  isDigitalVerified: boolean | null;
};

export type ComparisonTypeTemplatesResponse = {
  type: MatchingRuleCriteriaType;
  comparisonTypes: CriteriaComparisonTypeResponse[] | null;
};

export enum MatchingPolicyGroupType {
  Full = 'Full',
  Possible = 'Possible',
}
export type MatchingPolicyGroupTemplateResponse = {
  type: MatchingPolicyGroupType;
  matchingRules: MatchingRuleResponse[];
};

export type MatchingPolicyGroupResponse = {
  id: string;
  type: MatchingPolicyGroupType;
  matchingRules: MatchingRuleResponse[];
};

export type CreatePolicyDto = {
  name: string;
  type: string;
  companyId: string;
  description?: string;
};

export type MatchingPolicyCreateResponse = {
  matchingPolicyId: string;
  matchingPolicyVersionId: string;
};

export type MatchingRuleCriteriaDto = {
  type: MatchingRuleCriteriaType;
  comparisonType: MatchingRuleCriteriaComparisonType;
  mustBeVerifiedByDigitalIdentityService: boolean;
  associationConfirmed?: boolean;
};

export type CreateMatchingRuleDto = {
  matchingPolicyGroupId: string;
  name: string;
  matchingRuleCriteries: MatchingRuleCriteriaDto[];
  companyId: string;
};

export type UpdateMatchingRuleDto = {
  name: string;
  matchingRuleCriteries: MatchingRuleCriteriaDto[];
  companyId: string;
};

export type ConfirmPolicyDto = {
  policyId: string;
  companyId: string;
  schemeIds?: string[];
  comment?: string;
};

export type AddRuleToPolicyDto = {
  policyId: string;
  companyId: string;
  fullMatchingRules: string[];
  possibleMatchingRules: string[];
};

export type MatchingPolicyWithRuleResponse = {
  id: string;
  name: string;
  status: MatchingPolicyStatus;
  version: number;
  description: string | null;
  type: MatchingPolicyType;
  matchingPolicyGroups: MatchingPolicyGroupResponse[];
  canEditPolicy: boolean;
  isSelfApproval: boolean;
};

export type MatchingPolicyVersionWithRuleResponse = {
  id: string;
  matchingPolicyId: string;
  matchingPolicyName: string;
  matchingPolicyType: MatchingPolicyType;
  createdByUserId: string | null;
  reviewerUserId: string | null;
  status: MatchingPolicyApprovalStatus;
  version: number;
  createdByUserName: string | null;
  reviewerUserName: string | null;
  createdDate: string;
  updatedDate: string | null;
  creatorComment: string | null;
  reviewerComment: string | null;
  matchingPolicyGroups: MatchingPolicyGroupResponse[];
  canEditPolicy: boolean;
};

export type MatchingPolicyVersionSyncResponse = {
  id: string;
  status: MatchingPolicySyncStatus;
  createdDate: string;
  updatedDate: string | null;
  errorMessage: string | null;
};

export type MatchingPolicyVersionDetailsResponse = {
  id: string;
  status: MatchingPolicyApprovalStatus;
  version: number;
  createdByUserName: string | null;
  reviewerUserName: string | null;
  createdDate: string;
  updatedDate: string | null;
  matchingPolicyId: string;
  matchingPolicyName: string | null;
  creatorComment: string | null;
  reviewerComment: string | null;
  reviewers: string[] | null;
  matchingPolicyGroups: MatchingPolicyGroupResponse[];
  isEnabledToApproval: boolean;
  isSelfApproval: boolean;
  isEnableToSync: boolean;
  matchingPolicyVersionSync: MatchingPolicyVersionSyncResponse;
};

export type MatchingPolicyShortResponse = {
  id: string;
  name: string | null;
  status: MatchingPolicyStatus;
  type: MatchingPolicyType;
  createdDate: string;
  updatedDate: string | null;
};

export type MatchingPolicyLastVersionsResponse = {
  matchingPolicyId: string;
  policyName: string;
  matchingPolicyApprovedVersionId: string;
  matchingPolicyPendingVersionId: string;
};

export enum SearchMatchingStatus {
  FullMatch = 'FullMatch',
  PossibleMatch = 'PossibleMatch',
  FullMatchAndPossibleMatch = 'FullMatchAndPossibleMatch',
  NoMatch = 'NoMatch',
}

export enum SearchPensionValue {
  ERIAvailable = 'ERIAvailable',
  ERIMissing = 'ERIMissing',
  AccruedPensionAvailable = 'AccruedPensionAvailable',
  AccruedPensionMissing = 'AccruedPensionMissing',
  BothAvailable = 'BothAvailable',
  BothMissing = 'BothMissing',
}

export enum MembershipPensionValue {
  Missing = 'Missing',
  Available = 'Available',
}

export enum AltusAddressType {
  C = 'C',
  O = 'O',
  P = 'P',
}

export enum SectionPensionStatus {
  IWU = 'IWU',
  IPPF = 'IPPF',
}

export enum AdditionalSchemeType {
  OTH = 'OTH',
  FOR = 'FOR',
}

export type MembershipAccountDetailsDto = {
  sourceMembershipID: string | null;
  pensionRef: string | null;
  pensionName: string | null;
  additionalPensionName: string | null;
  additionalPensionType: AdditionalSchemeType;
  pensionType: PensionType;
  pensionOrigin: OriginType;
  pensionStatus: MembershipStatusCode;
  sectionPensionStatus: SectionPensionStatus | null;
  pensionStartDate: string;
  pensionRetirementDate: string | null;
  pensionLink: string | null;
  schemePensionLink: string | null;
};

export type AltusTypeBase = {
  type: string;
  description: string;
  altusName: string;
};

export type AvailablePensionTypes = {
  pensionTypes: AltusTypeBase[] | null;
  pensionOrigins: AltusTypeBase[] | null;
  pensionStatuses: AltusTypeBase[] | null;
};

export type MembershipStatuses = {
  pensionStatus: PensionStatus;
  pensionType: PensionType;
};

export type MemberSearchResponse = {
  membershipId: string;
  membershipSrcId: string | null;
  pensionRef: string | null;
  firstName: string | null;
  lastName: string | null;
  nino: string | null;
  dateOfBirth: string | null;
  schemeName: string | null;
  matchingStatus: SearchMatchingStatus;
  matchingOverrideStatus: MatchingOverrideStatus | null;
  possibleMatchCaseRef: string | null;
  accruedPensionValue: MembershipPensionValue;
  eriPensionValue: MembershipPensionValue;
  pdpStatus: PDPStatus;
};

export type MemberSearchResponsePagedResponse = {
  totalCount: number;
  items: MemberSearchResponse[];
};

export type MembershipAddressDto = {
  addressId: string;
  addressType?: AddressTypeEnum;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  addressLine4?: string;
  addressLine5?: string;
  country?: string;
  postCode?: string;
};

export type AltSurnameDto = {
  id?: string;
  name: string;
  type: AltNameType;
};

export type AltForenameDto = {
  id?: string;
  name: string;
};

export type AltEmailDto = {
  id?: string;
  email: string;
};

export type AltPhoneDto = {
  id?: string;
  number: string;
};

export type MembershipDetailDto = {
  companyId: string;
  membershipId: string;
  pensionRef?: string | null;
  membershipSrcId?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  dateOfBirth?: string | null;
  email?: string | null;
  altSurnames?: AltSurnameDto[] | null;
  altForenames?: AltForenameDto[] | null;
  altEmails?: AltEmailDto[] | null;
  altPhones?: AltPhoneDto[] | null;
  addresses?: MembershipAddressDto[];
  nino?: string | null;
  nationalInsuranceNumberIsNotApplicable: boolean;
  fullName?: string;
  mobile?: string | null;
  updateComment?: string;
};

export enum PDPStatus {
  Active = 'Active',
  CALL = 'CALL',
  NEW = 'NEW',
}

export type MembershipPDPStatusResponse = {
  pdpAvailable: boolean;
  pdpStatus: PDPStatus;
  pdpStatusEditComment: string | null;
};

export type EditPDPStatusDto = {
  companyId: string;
  membershipId: string;
  pdpAvailable?: boolean;
  pdpStatus?: PDPStatus;
  pdpStatusEditComment?: string;
};

export enum MatchingOverrideStatus {
  ExactMatchesOnly = 'ExactMatchesOnly',
  ExactMatchDowngrade = 'ExactMatchDowngrade',
}

export type MembershipMatchingOverrideResponse = {
  status: MatchingOverrideStatus;
  comment: string | null;
};

export type EditMatchingOverrideStatusModel = {
  companyId: string;
  membershipId: string;
  status: MatchingOverrideStatus | null;
  comment: string;
};

export enum MembershipMatchingType {
  Full = 'Full',
  Possible = 'Possible',
}

export enum MembershipMatchingStatus {
  Active = 'Active',
  Expired = 'Expired',
  Deregistered = 'Deregistered',
  Demoted = 'Demoted',
  Investigating = 'Investigating',
}

export enum RepeatedMatchOutcomeReason {
  Rematched = 'Rematched',
  RemainedPossibleMatch = 'RemainedPossibleMatch',
}

export type MembershipMatchingDto = {
  membershipMatchingId: string;
  membershipId: string;
  status: MembershipMatchingStatus;
  matchType: MembershipMatchingType;
  possibleMatchCaseRef: string | null;
  findCorrelationId: string | null;
  repeatedMatchReason: RepeatedMatchOutcomeReason | null;
  pei: string | null;
  matchingDate: string;
  expiryDate: string | null;
  fullName: string | null;
};

export type DashboardAddressData = {
  countryCode: string | null;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  line4: string | null;
  line5: string | null;
  postcode: string | null;
  addressType: AddressTypeEnum | null;
  isAddressVerified: boolean;
};

export type AdminSourceAddressData = {
  countryCode?: string | null;
  line1?: string | null;
  line2?: string | null;
  line3?: string | null;
  line4?: string | null;
  line5?: string | null;
  postcode?: string | null;
  addressType: AddressTypeEnum | null;
};

export type DashboardAlternateSurnameData = {
  name: string | null;
  nameType: string | null;
  isNameVerified: boolean;
};

export type AdminSourceAltSurnameData = {
  name: string;
  nameType: AltNameType;
};

export type DashboardAlternateForenameData = {
  name: string | null;
  isNameVerified: boolean;
};

export type AdminSourceAltForenameData = {
  name: string;
};

export type DashboardAlternateEmailData = {
  emailAddress: string | null;
  isEmailAddressVerified: boolean;
};

export type AdminSourceAltEmailData = {
  email: string;
};

export type DashboardAlternatePhoneData = {
  phoneNumber: string | null;
  isPhoneNumberVerified: boolean;
};

export type AdminSourceAltPhoneNumberData = {
  phoneNumber: string;
};

export type DashboardDataDto = {
  forename: string | null;
  surname: string | null;
  dateOfBirth: string;
  email: string | null;
  phoneNumber: string | null;
  nationalInsuranceNumber: string | null;
  hasNoNationalInsuranceNumber: boolean;
  isDateOfBirthVerified: boolean;
  isEmailAddressVerified: boolean;
  isForenameVerified: boolean;
  isHasNoNationalInsuranceNumberVerified: boolean;
  isPhoneNumberVerified: boolean;
  isNationalInsuranceNumberVerified: boolean;
  isSurnameVerified: boolean;
  addresses: DashboardAddressData[];
  alternateSurnames: DashboardAlternateSurnameData[] | null;
  alternateForenames: DashboardAlternateForenameData[] | null;
  alternateEmails: DashboardAlternateEmailData[] | null;
  alternatePhones: DashboardAlternatePhoneData[] | null;
};

export type AdminSourceDataDto = {
  forename: string;
  surname: string;
  dateOfBirth: string;
  email?: string | null;
  hasNoNationalInsuranceNumber: boolean;
  isNationalInsuranceNumberValid: boolean;
  nationalInsuranceNumber?: string | null;
  phoneNumber?: string | null;
  addresses: AdminSourceAddressData[] | null;
  altSurnames: AdminSourceAltSurnameData[] | null;
  altForenames: AdminSourceAltForenameData[] | null;
  altEmails: AdminSourceAltEmailData[] | null;
  altPhoneNumbers: AdminSourceAltPhoneNumberData[] | null;
};

export type MembershipMatchingDetailsDto = {
  membershipMatchingId: string;
  membershipId: string;
  peiNo: string | null;
  registrationId: number | null;
  findCorrelationId: string | null;
  possibleMatchCaseRef: string | null;
  matchingPolicyName: string | null;
  matchingPolicyVersion: number;
  matchingPolicyStatus: MatchingPolicyApprovalStatus;
  matchingType: MembershipMatchingType;
  repeatedMatchReason: RepeatedMatchOutcomeReason | null;
  rulesUsed: RulesUsedDto[] | null;
  dashboardData: DashboardDataDto | null;
  adminSourceData: AdminSourceDataDto | null;
  altusError: string | null;
  fullName: string | null;
};

export enum MembershipAuditType {
  NotAvailable = 'NotAvailable',
  DirectUpdate = 'DirectUpdate',
}

export type MembershipAuditDto = {
  id: string;
  schemeId: string;
  operationId: string | null;
  userName: string | null;
  changeDate: string;
  method: string | null;
  syncStatus: AltusSyncStatus | null;
  schemeName: string | null;
  pensionReference: string | null;
};

export type MembershipAuditDtoPagedResponse = {
  totalCount: number;
  items: MembershipAuditDto[];
};

export type DataChangeAuditInfo = {
  uploadId: string | null;
  pensionReference: string | null;
  schemeName: string | null;
  dataChangeAuditUserName: string | null;
  dataChangeAuditChangeDate: string;
  comments: string | null;
};

export type PDPSynchronisationHistoryResponse = {
  batchId: string | null;
  type: AltusBatchType;
  status: AltusSyncStatus;
  message: string | null;
  createdDate: string;
  syncDate: string | null;
};

export type MembershipHistoryResponse = {
  membershipId: string;
  personId: string;
  sectionId: string;
  membershipSrcId: string | null;
  displayRef: string | null;
  statusCode: string | null;
  originType: string | null;
  dateJoinedScheme: string;
  targetRetirementDate: string | null;
  pensionLink: string | null;
  inaccCode: string | null;
  matchingOverrideStatus: MatchingOverrideStatus | null;
};

export enum DataChangeAction {
  Insert = 'Insert',
  Update = 'Update',
  Delete = 'Delete',
}
export type MembershipHistoryResponseHistoryResponse = {
  previousValue: MembershipHistoryResponse | null;
  updatedValue: MembershipHistoryResponse | null;
  action: DataChangeAction;
};

export type EmploymentsHistoryResponse = {
  employmentId: string;
  membershipId: string;
  employer: string | null;
  empStart: string | null;
  empEnd: string | null;
};

export type EmploymentsHistoryResponseHistoryResponse = {
  previousValue: EmploymentsHistoryResponse | null;
  updatedValue: EmploymentsHistoryResponse | null;
  action: DataChangeAction;
};

export type AccruedbenHistoryResponse = {
  accruedbenId: string;
  membershipId: string;
  benefitType: string | null;
  amountType: string | null;
  calcType: string | null;
  illustDate: string | null;
  payableDate: string | null;
  annualAmount: string | null;
  monthlyAmount: string | null;
  incomeEnd: string | null;
  increasing: boolean | null;
  hasSurvivorBen: boolean | null;
  pot: number | null;
  safeguarded: boolean | null;
  unavailCode: string | null;
  warnings: string | null;
};

export type AccruedbenHistoryResponseHistoryResponse = {
  previousValue: AccruedbenHistoryResponse | null;
  updatedValue: AccruedbenHistoryResponse | null;
  action: DataChangeAction;
};

export type EriHistoryResponse = {
  eriId: string;
  membershipId: string;
  benefitType: string | null;
  amountType: string | null;
  eriType: string | null;
  illustDate: string | null;
  payableDate: string | null;
  annualAmount: string | null;
  monthlyAmount: string | null;
  incomeEnd: string | null;
  increasing: string | null;
  hasSurvivorBen: boolean | null;
  pot: number | null;
  safeguarded: boolean | null;
  unavailCode: string | null;
  warnings: string | null;
};

export type EriHistoryResponseHistoryResponse = {
  previousValue: EriHistoryResponse | null;
  updatedValue: EriHistoryResponse | null;
  action: DataChangeAction;
};

export type MembershipMatchingsHistoryResponse = {
  membershipMatchingId: string;
  membershipId: string;
  matchingPolicyId: string;
  matchingRuleId: string;
  registrationId: number;
  findRequestId: string | null;
  status: string | null;
  matchType: string | null;
  pei: string | null;
  uniqueMemberReference: string | null;
  repeatedMatchReason: RepeatedMatchOutcomeReason;
  matchingDate: string;
  expiryDate: string | null;
  comment: string | null;
};

export type MembershipMatchingsHistoryResponseHistoryResponse = {
  previousValue: MembershipMatchingsHistoryResponse | null;
  updatedValue: MembershipMatchingsHistoryResponse | null;
  action: DataChangeAction;
};

export type PersonHistoryResponse = {
  personId: string;
  adminDataSourceId: string;
  personSrcId: string | null;
  forename: string | null;
  surname: string | null;
  dob: string;
  nino: string | null;
  ninoNotapplic: boolean;
  email: string | null;
  mobile: string | null;
};

export type PersonHistoryResponseHistoryResponse = {
  previousValue: PersonHistoryResponse | null;
  updatedValue: PersonHistoryResponse | null;
  action: DataChangeAction;
};

export type AltSurnameHistoryResponse = {
  altSurnameId: string;
  personId: string;
  type: string | null;
  name: string | null;
};

export type AltSurnameHistoryResponseHistoryResponse = {
  previousValue: AltSurnameHistoryResponse | null;
  updatedValue: AltSurnameHistoryResponse | null;
  action: DataChangeAction;
};

export type AltForenameHistoryResponse = {
  altForenameId: string;
  personId: string;
  name: string | null;
};

export type AltForenameHistoryResponseHistoryResponse = {
  previousValue: AltForenameHistoryResponse | null;
  updatedValue: AltForenameHistoryResponse | null;
  action: DataChangeAction;
};

export type AltEmailHistoryResponse = {
  altEmailId: string;
  personId: string;
  email: string | null;
};

export type AltEmailHistoryResponseHistoryResponse = {
  previousValue: AltEmailHistoryResponse | null;
  updatedValue: AltEmailHistoryResponse | null;
  action: DataChangeAction;
};

export type AltPhoneHistoryResponse = {
  altPhoneNoId: string;
  personId: string;
  number: string | null;
};

export type AltPhoneHistoryResponseHistoryResponse = {
  previousValue: AltPhoneHistoryResponse | null;
  updatedValue: AltPhoneHistoryResponse | null;
  action: DataChangeAction;
};

export type AddressHistoryResponse = {
  addressId: string;
  personId: string;
  addressType: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  addressLine3: string | null;
  addressLine4: string | null;
  addressLine5: string | null;
  postcode: string | null;
  countryCode: string | null;
};

export type AddressHistoryResponseHistoryResponse = {
  previousValue: AddressHistoryResponse | null;
  updatedValue: AddressHistoryResponse | null;
  action: DataChangeAction;
};

export type SummaryHistoryResponse = {
  membershipHistory: MembershipHistoryResponseHistoryResponse;
  employmentsHistory: EmploymentsHistoryResponseHistoryResponse[] | null;
  accruedbenHistory: AccruedbenHistoryResponseHistoryResponse[] | null;
  eriHistory: EriHistoryResponseHistoryResponse | null;
  membershipMatchingsHistory: MembershipMatchingsHistoryResponseHistoryResponse[] | null;
  personHistory: PersonHistoryResponseHistoryResponse;
  altSurnamesHistory: AltSurnameHistoryResponseHistoryResponse[] | null;
  altForenamesHistory: AltForenameHistoryResponseHistoryResponse[] | null;
  altEmailsHistory: AltEmailHistoryResponseHistoryResponse[] | null;
  altPhonesHistory: AltPhoneHistoryResponseHistoryResponse[] | null;
  addressHistory: AddressHistoryResponseHistoryResponse[] | null;
};

export type MembershipAuditHistoryDto = {
  membershipAuditId: string;
  generalInformation: DataChangeAuditInfo;
  pdpSynchronisationHistory: PDPSynchronisationHistoryResponse | null;
  summaryHistory: SummaryHistoryResponse;
  method: string | null;
};

export type RulesUsedDto = {
  ruleId: string;
  ruleName: string | null;
  types: string[];
};

export enum MatchResolutionType {
  Pending = 'Pending',
  InReview = 'InReview',
  Overdue = 'Overdue',
  Resolved = 'Resolved',
}

export type PossibleMatchSearchResponse = {
  membershipId: string;
  schemeName: string | null;
  pei: string | null;
  pensionReference: string | null;
  pdpMemberReference: string | null;
  matchingDate: string;
  expireDate: string;
  resolutionType: MatchResolutionType;
};

export type PossibleMatchResponseWithCount = {
  totalCount: number;
  items: PossibleMatchSearchResponse[];
};

export type AdminDataSourceDto = {
  id: string;
  name: string | null;
};

export type FindRequestStatistic = {
  timestamp: string;
  count: number;
};

export type FindAndViewRequestsStatistic = {
  timestamp: string;
  countFind: CountFindByOutcome;
  countView: number;
};

export type CountFindByOutcome = {
  full: number;
  possible: number;
};

export type MembershipAnalyticsResponse = {
  totalMembers: number;
  totalSchemes: number;
  statuses: MemberCountByGroup[];
  pensionTypes: MemberCountByGroup[];
};

export type MemberCountByGroup = {
  name: string | null;
  totalMembers: number;
};

export type GetAnalyticsModel = {
  companyId: string;
  schemeId?: string;
};

export enum UploadType {
  SFTP = 'SFTP',
  API = 'API',
  Manual = 'Manual',
}

export enum DataUploadStatusDto {
  InProgress = 'InProgress',
  UploadSuccessful = 'UploadSuccessful',
  PartiallyUploaded = 'PartiallyUploaded',
  UploadFailed = 'UploadFailed',
}

export enum DataUploadPdpStatus {
  NotStarted = 'NotStarted',
  Success = 'Success',
  Failed = 'Failed',
  InProgress = 'InProgress',
  PartiallySuccessful = 'PartiallySuccessful',
  NoData = 'NoData',
  Disabled = 'Disabled',
}

export type DataUploadDto = {
  id: string;
  name: string | null;
  uploadType: UploadType;
  mappingKey: string | null;
  uploadedDate: string;
  uploadedBy: string | null;
  schemesAffected: string | null;
  status: DataUploadStatusDto;
  pdpStatus: DataUploadPdpStatus;
};

export type DataUploadDtosWithCount = {
  totalCount: number;
  items: DataUploadDto[];
};

export type SectionOptionDto = {
  id: string | null;
  name: string | null;
};

export type SchemeOptionDto = {
  id: string | null;
  name: string | null;
  sections: SectionOptionDto[] | null;
};

export type DataUploadSchemeSectionOptionsDto = {
  schemes: SchemeOptionDto[] | null;
};

export enum DataUploadStatus {
  RawDataReceived = 'RawDataReceived',
  RawDataMappedToSQL = 'RawDataMappedToSQL',
  MappingProjectRunning = 'MappingProjectRunning',
  StrictApiInProgress = 'StrictApiInProgress',
  PartiallyUploaded = 'PartiallyUploaded',
  FullyUploaded = 'FullyUploaded',
  Failed = 'Failed',
}
export type DataUploadAuditDto = {
  id: string;
  status: DataUploadStatus;
  description: string | null;
  timestamp: string;
};

export type DataUploadDetailsDto = {
  id: string;
  name: string | null;
  uploadType: UploadType;
  uploadedDate: string;
  uploadedBy: string | null;
  schemesAffected: string;
  membersProcessedFully: number | null;
  membersProcessedPartially: number | null;
  membersFailed: number | null;
  totalMembers: number | null;
  status: DataUploadStatusDto;
  processedDate: string | null;
  dataUploadAudits: DataUploadAuditDto[] | null;
};

export enum DataUploadValidationResult {
  Blocked = 'Blocked',
  NotBlocked = 'Not blocked',
}

export type DataUploadCheckHit = {
  dataUploadId: string;
  personSrcId: string | null;
  membershipSrcId: string | null;
  displayRef: string | null;
  originalValue: string | null;
  schemeName: string | null;
  sectionName: string | null;
  areaValidated: string | null;
  fieldValidated: string | null;
  description: string | null;
  result: DataUploadValidationResult | null;
};

export type DataUploadCheckHitsWithCount = {
  totalCount: number;
  items: DataUploadCheckHit[];
};

export enum AltusSyncStatus {
  None = 'None',
  NotStarted = 'NotStarted',
  PendingProcessing = 'PendingProcessing',
  Processed = 'Processed',
  Failed = 'Failed',
  Aborted = 'Aborted',
  ProcessedWithErrors = 'ProcessedWithErrors',
  Processing = 'Processing',
}

export enum AltusBatchType {
  Pensions = 'Pensions',
  Valuations = 'Valuations',
}

export enum AltusSyncType {
  Update = 'Update',
  Delete = 'Delete',
}

export type SearchDataUploadSyncResponse = {
  batchId: string;
  batchType: AltusBatchType;
  syncStatus: AltusSyncStatus;
  syncType: AltusSyncType;
  message: string | null;
  totalRecords: number;
  createdDate: string;
  syncDate: string | null;
};

export type DataUploadSyncWithCount = {
  totalCount: number;
  items: SearchDataUploadSyncResponse[];
};

export type DataUploadSyncDetailsResponse = {
  id: string;
  type: AltusBatchType;
  syncStatus: AltusSyncStatus;
  syncType: AltusSyncType;
  message: string;
};

export type DataUploadSyncDetailsPensionResponse = {
  personSrcId: string | null;
  membershipSrcId: string | null;
  membershipId: string | null;
  pensionReference: string | null;
};

export enum EriTypeEnum {
  BS = 'BS',
  CBI = 'CBI',
  SMPI = 'SMPI',
}

export enum BenefitTypeEnum {
  AVC = 'AVC',
  CBL = 'CBL',
  CBS = 'CBS',
  CDI = 'CDI',
  CDL = 'CDL',
  DB = 'DB',
  DBL = 'DBL',
  DC = 'DC',
}

export enum AmountTypeEnum {
  CSH = 'CSH',
  CSHL = 'CSHL',
  CSHN = 'CSHN',
  INC = 'INC',
  INCL = 'INCL',
  INCN = 'INCN',
}

export enum CalcType {
  SMPI = 'SMPI',
  CBI = 'CBI',
  BS = 'BS',
  SM = 'SM',
}

export enum UnavailCodeEnum {
  ANO = 'ANO',
  DB = 'DB',
  DBC = 'DBC',
  DCC = 'DCC',
  DCHA = 'DCHA',
  DCHP = 'DCHP',
  MEM = 'MEM',
  NET = 'NET',
  NEW = 'NEW',
  PPF = 'PPF',
  TRN = 'TRN',
  WU = 'WU',
}

export enum WarningCodeEnum {
  AVC = 'AVC',
  CUR = 'CUR',
  DEF = 'DEF',
  FAS = 'FAS',
  PEO = 'PEO',
  PNR = 'PNR',
  PSO = 'PSO',
  SCP = 'SCP',
  TVI = 'TVI',
  UNP = 'UNP',
}

export type EriShortDto = {
  id: string;
  benefitType: BenefitTypeEnum;
  amountType: AmountTypeEnum | null;
  eriType: EriTypeEnum;
  annualAmount: number | null;
  monthlyAmount: number | null;
  pot: number | null;
  nominalAmount: boolean | null;
  illustDate: string | null;
  groupSrcId: string | null;
  unavailCode: UnavailCodeEnum | null;
  unavailDescription: string | null;
};

export type EriWarningDto = {
  id: string;
  warningCode: WarningCodeEnum;
  description: string | null;
};

export type EriDetailsDto = EriShortDto & {
  incomeEnd: string | null;
  increasing: boolean | null;
  hasSurvivorBen: boolean | null;
  safeguarded: boolean | null;
  payableDate: string | null;
  pensionStatus: PensionStatus;
  pensionType: PensionType;
  eriWarnings: EriWarningDto[] | null;
};

export type EriWarningUpdateDto = {
  warningCode: WarningCodeEnum;
};

export type EriCreateDto = {
  benefitType: BenefitTypeEnum;
  amountType?: AmountTypeEnum;
  eriType: EriTypeEnum;
  illustDate?: string;
  payableDate?: string;
  annualAmount?: number;
  monthlyAmount?: number;
  nominalAmount?: boolean;
  incomeEnd?: string;
  increasing?: boolean;
  hasSurvivorBen?: boolean;
  pot?: number;
  safeguarded?: boolean;
  groupSrcId?: string;
  unavailCode?: UnavailCodeEnum;
  eriWarnings?: EriWarningUpdateDto[];
};

export type EriUpdateDto = EriCreateDto & {
  id: string;
  companyId: string;
  updateComment: string;
};

export type BulkCreateEriDto = {
  companyId: string;
  membershipId: string;
  eriCreateDtos: EriCreateDto[];
};

export type AccruedbenShortDto = {
  id: string;
  benefitType: BenefitTypeEnum;
  amountType: AmountTypeEnum | null;
  calcType: CalcType;
  annualAmount: number | null;
  monthlyAmount: number | null;
  pot: number | null;
  nominalAmount: boolean | null;
  illustDate: string | null;
  groupSrcId: string | null;
  unavailCode: UnavailCodeEnum | null;
  unavailDescription: string | null;
};

export type AccruedbenWarningDetailsDto = EriWarningDto;

export type AccruedbenDetailsDto = AccruedbenShortDto & {
  incomeEnd: string | null;
  increasing: boolean | null;
  hasSurvivorBen: boolean | null;
  safeguarded: boolean | null;
  payableDate: string | null;
  accruedbenWarnings: AccruedbenWarningDetailsDto[] | null;
};

export type AccruedbenWarningDto = EriWarningUpdateDto;

export type AccruedbenCreateDto = {
  benefitType: BenefitTypeEnum;
  amountType?: AmountTypeEnum;
  calcType: CalcType;
  illustDate?: string;
  payableDate?: string;
  annualAmount?: number;
  monthlyAmount?: number;
  nominalAmount?: boolean;
  incomeEnd?: string;
  increasing?: boolean;
  hasSurvivorBen?: boolean;
  pot?: number;
  safeguarded?: boolean;
  groupSrcId?: string;
  unavailCode?: UnavailCodeEnum;
  accruedbenWarnings?: AccruedbenWarningDto[];
};

export type AccruedbenUpdateDto = AccruedbenCreateDto & {
  id: string;
  companyId: string;
  updateComment: string;
};

export type BulkCreateAccruedbenDto = {
  companyId: string;
  membershipId: string;
  accruedbenCreateDtos: AccruedbenCreateDto[];
};

export type EmploymentDto = {
  employer: string;
  empStart: string;
  empEnd: string;
  employerType: string;
};
export enum TriggerReason {
  Missing = 'Missing',
  AwaitingDBC = 'AwaitingDBC',
  AwaitingDCC = 'AwaitingDCC',
  OutsideSLA = 'OutsideSLA',
}

export enum ViewDataType {
  ERI = 'ERI',
  AccruedPension = 'AccruedPension',
}

export enum SlaStatus {
  Overdue = 'Overdue',
  DueToday = 'DueToday',
  OnTime = 'OnTime',
}

export enum MissingViewDataStatus {
  Open = 'Open',
  UnderReview = 'UnderReview',
  Aborted = 'Aborted',
  Completed = 'Completed',
}

export type MissingViewValueDto = {
  id: string;
  viewDataType: ViewDataType;
  pensionReference: string | null;
  membershipId: string;
  schemeName: string | null;
  schemeId: string;
  viewDataRequestDate: string | null;
  dueDataProvidingDate: string;
  matchingRequestDate: string | null;
  status: MissingViewDataStatus;
  resolutionDate: string | null;
  pdpReported: boolean;
  slaCountdown: number;
  slaStatus: SlaStatus | null;
  unavailCode: string | null;
  comment: string | null;
  illustDate: string | null;
  illustCountdown: number;
};

export type MissingViewValueDtoPagedResponse = {
  totalCount: number;
  items: MissingViewValueDto[] | null;
};

export type ChangeMissingViewDataStatusModel = {
  status: MissingViewDataStatus;
  comment?: string;
};

export enum ReportType {
  FullScheme = 'FullScheme',
  MembershipData = 'MembershipData',
  MiData = 'MiData',
  MatchingResults = 'MatchingResults',
  MissingPensionValueData = 'MissingPensionValueData',
  MembershipDetails = 'MembershipDetails',
  DigitallyVerifiedPersonalData = 'DigitallyVerifiedPersonalData',
}

export enum ExtractStatus {
  Created = 'Created',
  InProgress = 'InProgress',
  Success = 'Success',
  Failed = 'Failed',
}

export type FilterDates = {
  dateFrom: string;
  dateTo: string;
};

export type DataExtractRequest = {
  companyId: string;
  schemeIds: string[];
  reportType: ReportType;
  filter?: FilterDates;
};

export type DataExtractSchemeResponse = {
  schemeId: string;
  isSucceed: boolean;
  errorMessage: string | null;
  resultErrorType: ResultFailType;
};

export type DataExtractResponse = {
  schemes: DataExtractSchemeResponse[] | null;
};

export type SearchDataExtractAuditResponse = {
  id: string;
  userName: string | null;
  reportType: ReportType;
  status: ExtractStatus;
  schemeId: string;
  schemeName: string | null;
  createdDate: string;
  filter: FilterDates | null;
  errorMessage: string | null;
  isDownloadable: boolean;
  fileSizeBytes: number | null;
  fileName: string | null;
};

export type SearchDataExtractAuditResponsePagedResponse = {
  totalCount: number;
  items: SearchDataExtractAuditResponse[];
};

export type DataExtractAuditResponse = {
  id: string;
  userId: string;
  userName: string | null;
  reportType: ReportType;
  status: ExtractStatus;
  schemeId: string;
  schemeName: string | null;
  errorMessage: string | null;
  createdDate: string;
  isDownloadable: boolean;
};

export type DataExtractDownloadAuditResponse = {
  id: string;
  userId: string;
  userName: string | null;
  downloadDate: string;
};

export type DataExtractDownloadAuditResponsePagedResponse = {
  totalCount: number;
  items: DataExtractDownloadAuditResponse[] | null;
};

export enum PDPRegistrationStatus {
  Draft = 'Draft',
  Success = 'Success',
  Initiated = 'Initiated',
  InProgress = 'InProgress',
  Failed = 'Failed',
}

export enum PDPRegulatorType {
  FCA = 'FCA',
  TPR = 'TPR',
}

export type PDPRegistrationModel = {
  companyId: string;
  status: PDPRegistrationStatus;
  pdpRegulatorType: PDPRegulatorType;
  schemeIds: string[];
  registrationDisplayName: string;
  registrationCode?: string;
  registrationNumber?: string;
  connectionDate?: string;
};

export type PDPRegistrationUpdateModel = PDPRegistrationModel & {
  pdpRegistrationId: string;
};

export type PDPRegistrationListItemDto = {
  id: string;
  registrationDisplayName: string | null;
  pdpRegulatorType: PDPRegulatorType;
  status: PDPRegistrationStatus;
  connectionDate: string | null;
  createdDate: string;
  updatedDate: string | null;
};

export type PDPRegistrationListItemDtoPagedResponse = {
  totalCount: number;
  items: PDPRegistrationListItemDto[];
};

export type PDPRegistrationDetailsSchemeDto = {
  id: string;
  name?: string | null;
};

export type PDPRegistrationDetailsDto = {
  companyId: string;
  companyName: string;
  status: PDPRegistrationStatus;
  pdpRegulatorType: PDPRegulatorType;
  schemes: PDPRegistrationDetailsSchemeDto[];
  registrationDisplayName: string | null;
  registrationCode: string | null;
  registrationNumber: string | null;
  connectionDate: string | null;
};

export type AutoDeregisterPossibleMatchesModel = {
  days: number;
};

export type MatchingPoliciesPublishChangesResponse = {
  id: string;
  companyId: string | null;
  name: string | null;
  status: MatchingPolicyStatus;
  type: MatchingPolicyType;
  version: string | null;
  description: string | null;
  createdDate: string;
  updatedDate: string | null;
};

export type EnvironmentConfigurationProductResponse = {
  productId: string;
  productName: string | null;
};

export type EnvConfigResponse = {
  id: string;
  name: string | null;
};

export type PublishMatchingPolicyChangesToEnvironmentModel = {
  environmentConfigurationId: string;
  comment: string;
  matchingPoliciesIds: string[];
};

export enum EnvironmentPublishArea {
  MatchingPolicies = 'MatchingPolicies',
}

export enum EnvironmentPublishStatus {
  InProgress = 'InProgress',
  Success = 'Success',
  Warning = 'Warning',
  Failed = 'Failed',
}

export type SearchEnvironmentPublishAuditResponse = {
  id: string;
  companyId: string | null;
  area: EnvironmentPublishArea;
  status: EnvironmentPublishStatus;
  userName: string | null;
  companyName: string | null;
  originEnvironment: string | null;
  destinationEnvironment: string | null;
  createDate: string;
};

export type SearchEnvironmentPublishAuditResponsePagedResponse = {
  totalCount: number;
  items: SearchEnvironmentPublishAuditResponse[] | null;
};

export type AuditPolicyResponse = {
  id: string;
  name: string | null;
  type: MatchingPolicyType;
  version: number;
};

export type EnvironmentPublishAuditResponse = SearchEnvironmentPublishAuditResponse & {
  comments: string | null;
  errors: string | null;
  policies: AuditPolicyResponse[] | null;
};

export type GetPDPRegistrationStatusModel = {
  companyId: string;
  ids: string[];
};

export type PDPRegistrationStatusDto = {
  id: string;
  status: PDPRegistrationStatus;
  updatedDate: string | null;
};

export type GetAltusSchemeStatusModel = {
  companyId: string;
  ids: string[];
};

export type GetAltusSchemeStatusResponse = {
  id: string;
  schemeStatus: GetAltusSchemeResponseStates;
};

export type SendViewRequestModel = {
  companyId: string;
  pensionRegistrationId: number;
};

export type MembershipDeleteModel = {
  comment: string;
};

export enum AltusSyncTriggerType {
  Default = 'Default',
  PDP = 'PDP',
  DataUpload = 'DataUpload',
}

export type SearchSyncResponse = {
  batchId: string;
  batchType: AltusBatchType;
  syncStatus: AltusSyncStatus;
  syncType: AltusSyncType;
  triggerType: AltusSyncTriggerType;
  message: string | null;
  createdDate: string;
  syncDate: string | null;
  totalRecords: number;
};

export type SearchSyncResponsePagedResponse = {
  totalCount: number;
  items: SearchSyncResponse[] | null;
};

export enum PDPSyncType {
  Scheme = 'Scheme',
  Section = 'Section',
  AdminContact = 'AdminContact',
  ManualSchemeResync = 'ManualSchemeResync',
}

export enum PDPSyncStatus {
  NotStarted = 'NotStarted',
  Success = 'Success',
  Failed = 'Failed',
}

export type PDPSyncAuditDto = {
  id: string;
  schemeId: string;
  schemeName: string | null;
  createdDate: string;
  updatedDate: string | null;
  type: PDPSyncType;
  status: PDPSyncStatus;
};

export type PDPSyncAuditDtoPagedResponse = {
  totalCount: number;
  items: PDPSyncAuditDto[] | null;
};

export type PDPSyncAuditDetailsResponse = {
  id: string;
  schemeId: string;
  schemeName: string | null;
  changedUserId: string;
  changedUserName: string | null;
  createdDate: string;
  updatedDate: string | null;
  type: PDPSyncType;
  status: PDPSyncStatus;
  errorMessage: string | null;
  data: string | null;
};

export type DataUploadSyncSummaryDto = {
  syncType: AltusSyncType;
  type: AltusBatchType;
  pending: number;
  succeeded: number;
  failed: number;
};

export type ResyncFailedDataUploadBatchesResponse = {
  batchIds: string[] | null;
};

export type GetSectionMembershipsCountResponse = {
  sectionId: string;
  sectionName: string | null;
  count: number;
  syncedCount: number;
  schemeStatus: string | null;
  pdpRegistrationStatus: string | null;
};

export type GetSchemeMembershipsCountResponse = {
  id: string;
  name: string | null;
  pdpRegistrationId: string;
  pdpRegistrationName: string | null;
  totalCount: number;
  totalSyncedCount: number;
  memberships: GetSectionMembershipsCountResponse[] | null;
};

export enum PDPConnectionAuditStatus {
  InProgress = 'InProgress',
  Success = 'Success',
  Failed = 'Failed',
}

export enum PDPConnectionAuditArea {
  ClientInformation = 'ClientInformation',
  PDPPublish = 'PDPPublish',
  PDPRegistration = 'PDPRegistration',
  EngineConfiguration = 'EngineConfiguration',
}

export enum PDPConnectionAuditAction {
  AddedClientInformation = 'AddedClientInformation',
  UpdatedClientInformation = 'UpdatedClientInformation',
  EnableDataSharingWithPDPToggle = 'EnableDataSharingWithPDPToggle',
  DisableDataSharingWithPDPToggle = 'DisableDataSharingWithPDPToggle',
  EnablePDPPublish = 'EnablePDPPublish',
  DisablePDPPublish = 'DisablePDPPublish',
  AddNewPDPRegistration = 'AddNewPDPRegistration',
  EditPDPRegistration = 'EditPDPRegistration',
  EnableAutoReRunMatchingEngine = 'EnableAutoReRunMatchingEngine',
  DisableAutoReRunMatchingEngine = 'DisableAutoReRunMatchingEngine',
  EnableAutoDeregisterPossibleMatches = 'EnableAutoDeregisterPossibleMatches',
  DisableAutoDeregisterPossibleMatches = 'DisableAutoDeregisterPossibleMatches',
  ChangeValuationAgeTriggerInMonths = 'ChangeValuationAgeTriggerInMonths',
  ResyncClientConfiguration = 'ResyncClientConfiguration',
}

export type PDPConnectionAuditResponse = {
  id: string;
  createdDate: string;
  status: PDPConnectionAuditStatus;
  area: PDPConnectionAuditArea;
  action: PDPConnectionAuditAction;
  description: string | null;
  changedUserName: string | null;
};

export type PDPConnectionAuditResponsePagedResponse = {
  totalCount: number;
  items: PDPConnectionAuditResponse[] | null;
};

export type UpdateCompanyMatchEngineConfigModel = {
  isAutoDeregisterPossibleMatchesEnabled: boolean;
  autoDeregisterPossibleMatchesDays: number;
  isMatchesAutoReRunEnabled: boolean;
  valuationAgeTriggerInMonths?: number;
  defaultDateFilterValue?: string;
  fullMatchNoValueDataAccruedResponse?: AccruedAmountUnavailabilityReason;
  fullMatchNoValueDataEriResponse?: EriUnavailabilityReason;
  fullMatchNoValueDataResponse?: FullMatchNoValueDataResponse;
  ppfNoValueDataResponse?: PpfNoValueDataResponse;
  viewValueGroupingIllustrationDatePreference?: ViewValueGroupingIllustrationDatePreference;
  viewValueGroupingOneOfEach?: boolean;
  viewValueGroupingTwoOfEachDifferentDates?: boolean;
  viewValueGroupingTwoOfEachSameDateTwoBenefitTypes?: boolean;
};

export enum DayOfWeek {
  Sunday = 'Sunday',
  Monday = 'Monday',
  Tuesday = 'Tuesday',
  Wednesday = 'Wednesday',
  Thursday = 'Thursday',
  Friday = 'Friday',
  Saturday = 'Saturday',
}

export type CalendarHolidayResponse = {
  id: string;
  title: string | null;
  date: string;
  dayOfWeek: DayOfWeek;
  notes: string | null;
  isEnabled: boolean;
};

export type AddCalendarHolidayModel = {
  title: string;
  date: string;
  notes?: string;
  isEnabled?: boolean;
};

export enum ExtractLogsType {
  ViewLogs = 'ViewLogs',
  FindLogs = 'FindLogs',
  PensionRegistrationLogs = 'PensionRegistrationLogs',
  DowntimeLogs = 'DowntimeLogs',
}

export type LogDataExtractResponse = {
  id: string;
  userId: string;
  userName: string | null;
  type: ExtractLogsType;
  status: ExtractStatus;
  createdDate: string;
  startDate: string | null;
  endDate: string | null;
  fileSizeBytes: number | null;
  isDownloadable: boolean;
  reason: string | null;
};

export type LogDataExtractResponsePagedResponse = {
  totalCount: number;
  items: LogDataExtractResponse[] | null;
};

export type LogDataExtractDownloadAuditResponse = {
  id: string;
  userName: string | null;
  downloadDate: string;
};

export type LogDataExtractDownloadAuditResponsePagedResponse = {
  totalCount: number;
  items: LogDataExtractDownloadAuditResponse[] | null;
};

export type LogDataExtractDetailsResponse = {
  id: string;
  userId: string;
  userName: string | null;
  type: ExtractLogsType;
  status: ExtractStatus;
  createdDate: string;
  startDate: string | null;
  endDate: string | null;
  fileSizeBytes: number | null;
  isDownloadable: boolean;
  reason: string | null;
  schemes: string | null;
};

export type FullSchemeResyncModel = {
  companyId: string;
  schemeIds?: string[];
};

export type TriggerCompanyIdsModel = {
  companyIds: string[];
};

export type FullSchemeResyncResponse = {
  batchIds: string[] | null;
};

export type AcceptedCompanyOperation = {
  companyId: string | null;
  operationId: string | null;
};

export type RejectedCompanyOperation = {
  companyId: string;
  reason: string | null;
};

export type MissingViewComparisonResponse = {
  acceptedCompanies: AcceptedCompanyOperation[] | null;
  rejectedCompanies: RejectedCompanyOperation[] | null;
  isFullAcceptance: boolean;
};

export type ClientConfigurationResyncAcceptedCompany = {
  companyId: string;
  configSyncId: string;
  auditId: string;
};

export type ClientConfigurationResyncResponse = {
  acceptedCompanies: ClientConfigurationResyncAcceptedCompany[] | null;
  rejectedCompanies: RejectedCompanyOperation[] | null;
  isFullAcceptance: boolean;
};

export type DeregisteredMembershipMatchingSyncResponse = {
  acceptedCompanies: AcceptedCompanyOperation[] | null;
  rejectedCompanies: RejectedCompanyOperation[] | null;
  isFullAcceptance: boolean;
};

export enum ResyncActionType {
  FullSchemeResync = 'FullSchemeResync',
  MissingViewComparison = 'MissingViewComparison',
  UpdateClientConfiguration = 'UpdateClientConfiguration',
  DeregisteredMembershipMatchingSync = 'DeregisteredMembershipMatchingSync',
}

export enum ResyncActionBatchStatus {
  InProgress = 'InProgress',
  Success = 'Success',
  Failed = 'Failed',
  CompletedWithErrors = 'CompletedWithErrors',
}

export type ResyncActionBatchResponse = {
  batchId: string;
  actionType: ResyncActionType;
  requestedByUserName: string | null;
  createdDate: string;
  updatedDate: string | null;
  status: ResyncActionBatchStatus;
  acceptedCount: number;
  rejectedCount: number;
  succeededCount: number;
  failedCount: number;
};

export type ResyncActionBatchResponsePagedResponse = {
  totalCount: number;
  items: ResyncActionBatchResponse[] | null;
};

export enum ResyncActionStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Success = 'Success',
  Failed = 'Failed',
}

export type ResyncActionBatchRejectedItemResponse = {
  companyId: string;
  reason: string | null;
};

export type ResyncActionBatchDetailsResponse = {
  batchId: string;
  actionType: ResyncActionType;
  requestedByUserName: string | null;
  createdDate: string;
  updatedDate: string | null;
  status: ResyncActionBatchStatus;
  acceptedCount: number;
  rejectedCount: number;
  succeededCount: number;
  failedCount: number;
  acceptedOperations: ResyncActionAuditDetailsResponse[] | null;
  rejectedItems: ResyncActionBatchRejectedItemResponse[] | null;
};

export type ResyncActionAuditDetailsResponse = {
  operationId: string;
  batchId: string;
  actionType: ResyncActionType;
  companyId: string | null;
  schemeId: string | null;
  requestedByUserName: string | null;
  createdDate: string;
  updatedDate: string | null;
  status: ResyncActionStatus;
  companyName: string | null;
  schemeName: string | null;
  requestedByUserId: string;
  traceId: string | null;
  errorMessage: string | null;
  executionId: string | null;
};

export type SchemeDeleteAuditResponse = {
  id: string;
  schemeId: string;
  schemeName: string | null;
  createdDate: string;
  altusDeleteSuccess: boolean | null;
  description: string | null;
  changedUserName: string | null;
  traceId: string | null;
};

export type SchemeDeleteAuditResponsePagedResponse = {
  totalCount: number;
  items: SchemeDeleteAuditResponse[] | null;
};

export type SectionDeleteAuditResponse = {
  id: string;
  schemeDeleteAuditId: string | null;
  sectionId: string;
  sectionName: string | null;
  sectionSrcId: string | null;
  createdDate: string;
  altusDeleteSuccess: boolean | null;
  companyId: string;
  schemeName: string | null;
  description: string | null;
  changedUserName: string | null;
  traceId: string | null;
};

export type SectionDeleteAuditResponsePagedResponse = {
  totalCount: number;
  items: SectionDeleteAuditResponse[] | null;
};

export enum PgpKeyType {
  Encrypt = 'Encrypt',
  Decrypt = 'Decrypt',
}

export type PgpKeyDto = {
  pgpKeyId: string;
  pgpKeyName: string | null;
  type: PgpKeyType;
};

export type SchemeKeyAssignmentDto = {
  schemeId: string;
  schemeName: string | null;
  isOverridden: boolean;
  overridePgpKeyId: string | null;
};

export type EncryptionAssignmentsResponse = {
  companyPgpKeyId: string | null;
  schemes: SchemeKeyAssignmentDto[] | null;
};

export type SchemeOverrideDto = {
  schemeId: string;
  pgpKeyId?: string;
};

export type SaveEncryptionAssignmentsDto = {
  companyId: string;
  companyPgpKeyId?: string;
  schemeOverrides?: SchemeOverrideDto[];
};

export type DataSourceKeyAssignmentDto = {
  dataSourceId: string;
  dataSourceName: string | null;
  isOverridden: boolean;
  overridePgpKeyId: string | null;
};

export type DecryptionAssignmentsResponse = {
  companyPgpKeyId: string | null;
  dataSources: DataSourceKeyAssignmentDto[] | null;
};

export type DataSourceOverrideDto = {
  dataSourceId: string;
  pgpKeyId?: string;
};

export type SaveDecryptionAssignmentsDto = {
  companyId: string;
  companyPgpKeyId?: string;
  dataSourceOverrides?: DataSourceOverrideDto[];
};
